import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { DocumentText, CloseCircle } from "iconsax-react";

interface TermsViewModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TermsViewModal: React.FC<TermsViewModalProps> = ({ isOpen, onClose }) => {
  const handleBackgroundClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[999] flex items-center justify-center bg-[#000000cc] p-4"
          onClick={handleBackgroundClick}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="relative flex flex-col bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between gap-3 p-3 border-b md:p-6 border-gorgonzola-blue-50">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center rounded-full size-10 md:size-12 min-w-10 md:min-w-12 bg-gorgonzola-blue-50">
                  <DocumentText className="text-gorgonzola-blue-300 size-5 md:size-6 min-w-5 md:min-w-6" variant="Bold" />
                </div>
                <div>
                  <h2 className="text-base font-inter-bold text-closed-shutter-400">Terms & Conditions for Shaku Embedded SDK</h2>
                  <p className="text-2xs text-closed-shutter-200">Last Updated: 12/10/2025</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="grid transition-colors duration-300 border rounded place-content-center size-10 border-gorgonzola-blue-50 text-closed-shutter-300 hover:text-gorgonzola-blue-300 hover:border-gorgonzola-blue-200"
              >
                <CloseCircle className="size-6" variant="Broken" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 p-3 overflow-y-auto md:p-6 custom_scroll">
              <div className="space-y-6 text-xs leading-relaxed text-closed-shutter-300">
                <p className="text-closed-shutter-400 font-inter-medium">
                  These Terms apply to users who sign in through Shaku's embedded SDK on our demo site or on Partner Sites. By signing in, you agree to these Terms.
                </p>

                <div>
                  <h3 className="mb-2 font-inter-bold text-closed-shutter-400">1. Service Description</h3>
                  <div className="pl-4 space-y-2">
                    <p>The Shaku SDK provides:</p>
                    <ul className="pl-4 space-y-1 list-disc">
                      <li>Secure user authentication</li>
                      <li>Access to measurement results (if you have an account)</li>
                      <li>Size recommendations on Partner Sites that use Shaku</li>
                      <li>Basic profile management</li>
                    </ul>
                    <p>Shaku does not own or sell clothing.</p>
                  </div>
                </div>

                <div>
                  <h3 className="mb-2 font-inter-bold text-closed-shutter-400">2. User Responsibilities</h3>
                  <div className="pl-4 space-y-2">
                    <p>By using our SDK, you agree:</p>
                    <ul className="pl-4 space-y-1 list-disc">
                      <li>To provide accurate information</li>
                      <li>Not to share your account details with others</li>
                      <li>Not to attempt unauthorized access, reverse engineering, or misuse of the SDK</li>
                    </ul>
                    <p>You are responsible for any activity under your account.</p>
                  </div>
                </div>

                <div>
                  <h3 className="mb-2 font-inter-bold text-closed-shutter-400">3. Size Recommendation Disclaimer</h3>
                  <div className="pl-4 space-y-2">
                    <p>Shaku uses AI-based estimations to suggest clothing sizes. However:</p>
                    <ul className="pl-4 space-y-1 list-disc">
                      <li>Size charts vary between brands</li>
                      <li>Fit preference is personal</li>
                      <li>Retail product data may change</li>
                    </ul>
                    <p>Therefore, Shaku is not responsible for incorrect size recommendations or return costs.</p>
                  </div>
                </div>

                <div>
                  <h3 className="mb-2 font-inter-bold text-closed-shutter-400">4. Affiliate Products & External Websites</h3>
                  <div className="pl-4 space-y-2">
                    <p>Partner Sites may show clothing powered by Shaku results. You acknowledge that:</p>
                    <ul className="pl-4 space-y-1 list-disc">
                      <li>Shaku does not manufacture or sell these clothes</li>
                      <li>Product details belong to the retailer</li>
                      <li>Shaku is not liable for purchases made outside our platform</li>
                      <li>Affiliate relationships may result in Shaku receiving commissions</li>
                    </ul>
                  </div>
                </div>

                <div>
                  <h3 className="mb-2 font-inter-bold text-closed-shutter-400">5. Account Security</h3>
                  <div className="pl-4 space-y-2">
                    <p>Shaku:</p>
                    <ul className="pl-4 space-y-1 list-disc">
                      <li>Stores passwords only in hashed (non-readable) form</li>
                      <li>Uses secure authentication practices</li>
                    </ul>
                    <p>Users must:</p>
                    <ul className="pl-4 space-y-1 list-disc">
                      <li>Maintain confidentiality of login credentials</li>
                      <li>Notify us if unauthorized access is suspected</li>
                    </ul>
                  </div>
                </div>

                <div>
                  <h3 className="mb-2 font-inter-bold text-closed-shutter-400">6. Data Processing & Privacy</h3>
                  <div className="pl-4 space-y-2">
                    <p>Your data is processed in accordance with our Privacy Policy, which complies with:</p>
                    <ul className="pl-4 space-y-1 list-disc">
                      <li>PIPEDA (Canada)</li>
                      <li>GDPR (EU)</li>
                      <li>Applicable provincial privacy laws</li>
                    </ul>
                    <p>Your data may be processed to provide accurate measurement and fitting recommendations.</p>
                  </div>
                </div>

                <div>
                  <h3 className="mb-2 font-inter-bold text-closed-shutter-400">7. Termination</h3>
                  <div className="pl-4 space-y-2">
                    <p>We may suspend or terminate your account if:</p>
                    <ul className="pl-4 space-y-1 list-disc">
                      <li>You violate these Terms</li>
                      <li>Fraud or misuse is detected</li>
                      <li>Required by law</li>
                    </ul>
                    <p>You may delete your account at any time.</p>
                  </div>
                </div>

                <div>
                  <h3 className="mb-2 font-inter-bold text-closed-shutter-400">8. Liability Limitations</h3>
                  <div className="pl-4 space-y-2">
                    <p>To the maximum extent permitted by law:</p>
                    <ul className="pl-4 space-y-1 list-disc">
                      <li>Shaku is not liable for indirect or consequential damages</li>
                      <li>Shaku is not liable for product issues from third-party retailers</li>
                    </ul>
                  </div>
                </div>

                <div>
                  <h3 className="mb-2 font-inter-bold text-closed-shutter-400">9. Governing Law</h3>
                  <p className="pl-4">These Terms are governed by the laws of Ontario, Canada, and applicable federal laws.</p>
                </div>

                <div>
                  <h3 className="mb-2 font-inter-bold text-closed-shutter-400">10. Contact</h3>
                  <p className="pl-4">For legal or account questions: 📧 support@shaku.tech</p>
                </div>

                <div className="p-4 rounded-lg bg-gorgonzola-blue-50">
                  <p className="text-closed-shutter-400">
                    <a href="mailto:support@shaku.tech" className="font-inter-medium text-gorgonzola-blue-300 hover:underline">
                      support@shaku.tech
                    </a>
                  </p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end p-3 border-t md:p-6 border-gorgonzola-blue-50">
              <button
                onClick={onClose}
                className="flex items-center justify-center gap-2 px-6 py-3 text-white transition-all duration-300 rounded-lg bg-gorgonzola-blue-300 hover:bg-gorgonzola-blue-400"
              >
                <span className="font-inter-medium">Close</span>
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default TermsViewModal;
